import PropTypes from 'prop-types';

/**
 * LoadingSpinner - A reusable loading spinner component
 * 
 * @param {string} size - Size variant: 'xs', 'sm', 'md', 'lg', 'xl' 
 * @param {string} color - Color variant: 'primary', 'secondary', 'white', 'gray', 'success', 'error', 'warning', 'info'
 * @param {boolean} inline - Whether to display inline or as block element
 * @param {string} className - Additional CSS classes
 * @param {Object} style - Inline styles
 */
const LoadingSpinner = ({ 
  size = 'md', 
  color = 'primary', 
  inline = false, 
  className = '', 
  style = {},
  ...props 
}) => {
  // Size classes
  const sizeClasses = {
    xxs: 'h-2 w-2 border-2',
    xs: 'h-3 w-3 border-2',
    sm: 'h-4 w-4 border-2',
    md: 'h-6 w-6 border-2',
    lg: 'h-8 w-8 border-[3px]',
    xl: 'h-10 w-10 border-[3px]'
  };

  // Color classes for the spinner
  const colorClasses = {
    primary: 'border-gray-200 border-t-[#70AE42]',
    secondary: 'border-gray-200 border-t-[#36404B]',
    white: 'border-white/30 border-t-white',
    gray: 'border-gray-200 border-t-gray-600',
    success: 'border-gray-200 border-t-green-500',
    error: 'border-gray-200 border-t-[#D9534F]',
    warning: 'border-gray-200 border-t-yellow-500',
    info: 'border-gray-200 border-t-blue-500',
    kran: 'border-gray-200 border-t-kran-primary',
  };

  // Dark theme color classes
  const darkColorClasses = {
    primary: 'dark:border-white/10 dark:border-t-[#70AE42]',
    secondary: 'dark:border-white/10 dark:border-t-[#36404B]',
    white: 'dark:border-white/30 dark:border-t-white',
    gray: 'dark:border-white/10 dark:border-t-gray-400',
    success: 'dark:border-white/10 dark:border-t-green-500',
    error: 'dark:border-white/10 dark:border-t-[#D9534F]',
    warning: 'dark:border-white/10 dark:border-t-yellow-500',
    info: 'dark:border-white/10 dark:border-t-blue-400',
    kran: 'dark:border-white/10 dark:border-t-kran-primary',
  };

  const spinnerClasses = [
    'animate-spin',
    'rounded-full',
    sizeClasses[size],
    colorClasses[color],
    darkColorClasses[color],
    inline ? 'inline-block align-middle mx-1' : 'block',
    className
  ].filter(Boolean).join(' ');

  return (
    <div 
      className={spinnerClasses}
      style={style}
      role="status"
      aria-label="Loading"
      {...props}
    >
      <span className="sr-only">Loading...</span>
    </div>
  );
};

LoadingSpinner.propTypes = {
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl', 'xxs']),
  color: PropTypes.oneOf(['primary', 'secondary', 'white', 'gray', 'success', 'error', 'warning', 'info', 'kran']),
  inline: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

export default LoadingSpinner; 